import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  EmptyState, FilterBar, ListTable, MetaFacts, MetricCard, Modal, PageHeader, Panel, SearchField, SegmentedControl,
  StatusChip, Timeline, ToolbarButton, type ListColumn, type StatusTone,
} from '../../ui-kit';
import { fetchDesk, type DeskLead, type DeskStage } from '../../services/salesDeskService';
import { fromNow, matches, useLive, when } from '../agents/rocco/live';

/**
 * The agency's sales desk: every lead the agents have touched, wherever it came
 * in, with where it has got to and what to do next. Leads from different agents
 * for the same person are one row. Updates by itself.
 */

const PERIODS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

const TONES: Record<DeskStage, StatusTone> = {
  new: 'neutral', talking: 'neutral', documents: 'warning', quoting: 'warning', quoted: 'good', ready: 'good', bound: 'good',
};

const UNASSIGNED = 'Unassigned';
const QUOTABLE = new Set(['Website chat', 'Carrier Quoting']);

/** 6145550113 → (614) 555-0113; anything else as it came. */
const phone = (value: string | null) => (value && /^\d{10}$/.test(value)
  ? `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}` : value);

const money = (value: number | null) => (value == null ? '' : `$${Math.round(value).toLocaleString()}`);

/** Due today or already late: a call-back somebody owes. */
function dueNow(lead: DeskLead, today: string): boolean {
  return !!lead.due_at && lead.due_at.slice(0, 10) <= today;
}

function counted<T>(values: T[]): Map<T, number> {
  const out = new Map<T, number>();
  values.forEach((v) => out.set(v, (out.get(v) ?? 0) + 1));
  return out;
}

export default function SalesDeskPage() {
  const navigate = useNavigate();
  const [days, setDays] = React.useState('30');
  const fetcher = React.useCallback(() => fetchDesk(Number(days)), [days]);
  const { data, error } = useLive(fetcher, 10_000);
  const [filters, setFilters] = React.useState<Record<string, string[]>>({});
  const [query, setQuery] = React.useState('');
  const [openId, setOpenId] = React.useState<string | null>(null);

  const all = data?.leads ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const pick = (key: string) => filters[key] ?? [];
  const rows = all.filter((l) => {
    const owner = l.owner || UNASSIGNED;
    return (!pick('owner').length || pick('owner').includes(owner))
      && (!pick('stage').length || pick('stage').includes(l.stage))
      && (!pick('from').length || pick('from').includes(l.came_from))
      && matches(query, [l.name, l.email, l.phone, l.owner, l.next, ...l.outside]);
  });
  // The figures follow the agent filter, so an agent sees their own day.
  const mine = all.filter((l) => !pick('owner').length || pick('owner').includes(l.owner || UNASSIGNED));

  const owners = counted(all.map((l) => l.owner || UNASSIGNED));
  const froms = counted(all.map((l) => l.came_from));
  const stages = counted(all.map((l) => l.stage));
  const fields = [
    {
      key: 'owner', label: 'Sales agent',
      options: Array.from(owners.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([value, count]) => ({ value, count })),
    },
    {
      key: 'stage', label: 'Stage',
      options: (data?.stages ?? []).map((s) => ({ value: s.key, label: s.label, count: stages.get(s.key) ?? 0 })),
    },
    {
      key: 'from', label: 'Lead source',
      options: Array.from(froms.entries()).sort((a, b) => a[0].localeCompare(b[0])).map(([value, count]) => ({ value, count })),
    },
  ];

  const loading = !data && !error;
  const figure = (n: number) => n.toLocaleString();
  const open = all.find((l) => l.id === openId) ?? null;

  const columns: ListColumn<DeskLead>[] = [
    {
      key: 'name', header: 'Lead', grow: 1, minWidth: 200, sortValue: (l) => l.name,
      render: (l) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{l.name}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
            {[phone(l.phone), l.email, l.state].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'stage', header: 'Stage', width: 150, sortValue: (l) => (data?.stages ?? []).findIndex((s) => s.key === l.stage),
      sortStartsDescending: true,
      render: (l) => <StatusChip tone={TONES[l.stage]} label={l.stage_label} />,
    },
    {
      key: 'next', header: 'Next step', grow: 1.2, minWidth: 220, sortValue: (l) => l.next,
      render: (l) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{l.next}</Typography>
          <Typography variant="caption" sx={(t) => ({
            color: dueNow(l, today) ? t.tokens.semantic.warning : t.tokens.text.tertiary,
          })}>
            {[l.due_at ? `Due ${when(l.due_at)}` : null,
              l.renews_on ? `Current policy ends ${l.renews_on}` : null].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'price', header: 'Best price', width: 130, align: 'right', sortValue: (l) => l.best_price,
      render: (l) => (
        <Stack alignItems="flex-end">
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>{money(l.best_price)}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{l.best_carrier ?? ''}</Typography>
        </Stack>
      ),
    },
    {
      key: 'owner', header: 'Sales agent', width: 170, sortValue: (l) => l.owner,
      render: (l) => (
        <Typography variant="body2" sx={(t) => ({ color: l.owner ? t.tokens.text.primary : t.tokens.text.tertiary })}>
          {l.owner || UNASSIGNED}
        </Typography>
      ),
    },
    {
      key: 'from', header: 'Lead source', width: 190, sortValue: (l) => l.came_from,
      render: (l) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{l.came_from}</Typography>
      ),
    },
    {
      key: 'last', header: 'Last activity', width: 140, sortValue: (l) => l.last_activity, sortStartsDescending: true,
      render: (l) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>{when(l.last_activity)}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{fromNow(l.last_activity)}</Typography>
        </Stack>
      ),
    },
  ];

  const quoteFrom = open?.records.find((r) => QUOTABLE.has(r.agent));
  const canQuote = !!open && !!quoteFrom && open.stage !== 'ready' && open.stage !== 'bound';

  return (
    <Box>
      <PageHeader
        title="Sales desk"
        description="Every lead our agents have worked on, wherever it came in, with where it has got to and what to do next."
        meta={data ? <MetaFacts facts={[{ key: 'n', label: `${all.length.toLocaleString()} leads` },
          { key: 'live', label: 'Updates by itself' }]} /> : null}
        actions={<SegmentedControl value={days} options={PERIODS} onChange={setDays} ariaLabel="Period" />}
      />
      {error && !data ? <Box sx={{ mt: 2 }}><EmptyState message={error} /></Box> : null}

      <Box sx={{ mt: 2.5, display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <MetricCard label="New leads" loading={loading} value={figure(mine.filter((l) => l.stage === 'new').length)}
          info="Leads nobody has spoken to or quoted yet." />
        <MetricCard label="Call-backs due" loading={loading} value={figure(mine.filter((l) => dueNow(l, today)).length)}
          info="Call-backs booked for today, or already late." />
        <MetricCard label="Being quoted" loading={loading}
          value={figure(mine.filter((l) => ['documents', 'quoting', 'quoted'].includes(l.stage)).length)}
          info="We have their documents, or a quote is under way or done." />
        <MetricCard label="Ready to bind" loading={loading} value={figure(mine.filter((l) => l.stage === 'ready').length)}
          info="A carrier website gave a price and we stopped before buying. A person binds it." />
        <MetricCard label="Bound" loading={loading} value={figure(mine.filter((l) => l.stage === 'bound').length)}
          info="Bound on the carrier's website by a person, after the agent stopped." />
      </Box>

      <Box sx={{ mt: 2.5 }}>
        <Panel title="Leads" subtitle={`${rows.length.toLocaleString()} of ${all.length.toLocaleString()} · most recent first`}
          search={<SearchField value={query} onChange={setQuery} placeholder="Search leads…" size="small" />}>
          <Stack spacing={2}>
            <FilterBar fields={fields} value={filters} onChange={setFilters} loading={loading} />
            <ListTable columns={columns} rows={rows} getRowKey={(l) => l.id} pageSize={25} loading={loading}
              defaultSort={{ key: 'last', direction: 'desc' }} onRowClick={(l) => setOpenId(l.id)}
              emptyMessage={all.length ? 'Nothing matches. Clear a filter or the search.'
                : 'No leads in this period. They appear here as the agents work.'} />
          </Stack>
        </Panel>
      </Box>

      <Modal open={!!open} onClose={() => setOpenId(null)} size="md" title={open?.name ?? ''}
        subtitle={open ? [open.stage_label, open.owner || UNASSIGNED, ...open.outside].join(' · ') : ''}
        footer={open ? (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {canQuote ? (
              <ToolbarButton onClick={() => navigate(`/agents/carrier_website/new?customer=${
                quoteFrom!.agent === 'Website chat' ? `chat:${quoteFrom!.ref}` : quoteFrom!.ref}`)}>
                Quote on a carrier website
              </ToolbarButton>
            ) : null}
            <ToolbarButton variant="ghost" onClick={() => navigate(open.next_link)}>Open in the agent</ToolbarButton>
          </Stack>
        ) : null}>
        {open ? (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary })}>Next step</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
                {[open.next, open.due_at ? `due ${when(open.due_at)}` : null].filter(Boolean).join(', ')}
              </Typography>
            </Box>
            <MetaFacts facts={[
              { key: 'phone', label: phone(open.phone) ?? '' },
              { key: 'email', label: open.email ?? '' },
              { key: 'state', label: open.state ?? '' },
              { key: 'renews', label: open.renews_on ? `Current policy ends ${open.renews_on}` : '' },
              { key: 'price', label: open.best_price != null ? `Best price ${money(open.best_price)} from ${open.best_carrier}` : '' },
            ]} />
            <Box>
              <Typography variant="subtitle2" sx={(t) => ({ color: t.tokens.text.primary, mb: 1 })}>What happened</Typography>
              <Timeline dense steps={open.steps.map((s, i) => ({
                key: `${i}`, title: s.text, meta: `${when(s.at)} · ${s.agent}`, tone: 'brand',
              }))} />
            </Box>
          </Stack>
        ) : null}
      </Modal>
    </Box>
  );
}
